import { Slot } from '@radix-ui/react-slot';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import * as React from 'react';

import { allRoutes } from '@/features/Routes/Routes';
import { cn } from '@/lib/utils';
import { Link, LinkProps, matchPath, useLocation } from 'react-router';

export default function BreadcrumbsImp() {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);

  // Split the current pathname into parts and build breadcrumbs from them
  const breadcrumbs = pathParts
    .map((_, index) => {
      // Reconstruct the path up to the current segment
      const to = '/' + pathParts.slice(0, index + 1).join('/');

      // Try to find a matching route from allRoutes where the path matches exactly
      const route = allRoutes.find((r) =>
        matchPath({ path: r.path, end: true }, to),
      );

      // If no matching route is found or the route doesn't define a breadcrumb, skip this part
      if (!route || !route.breadcrumb) return null;

      // Use the breadcrumb label from the route config
      const label = route.breadcrumb;

      // Return a breadcrumb object with the path and label
      return { to, label };
    })
    // Filter out any null values (i.e., unmatched or hidden breadcrumb segments)
    .filter((b) => !!b);

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <React.Fragment key={index}>
              <BreadcrumbItem className="sm:hidden md:block">
                {isLast ? (
                  <span
                    data-slot="breadcrumb-link"
                    className="text-primary transition-colors"
                  >
                    {breadcrumb.label}
                  </span>
                ) : (
                  <BreadcrumbLink to={breadcrumb.to}>
                    {breadcrumb.label}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && (
                <BreadcrumbSeparator className="sm:hidden md:block" />
              )}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm break-words sm:gap-2.5',
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
}

type BreadcrumbLinkProps = LinkProps & {
  asChild?: boolean;
  className?: string;
};

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : Link;

    return (
      <Comp
        ref={ref}
        data-slot="breadcrumb-link"
        className={cn('hover:text-foreground transition-colors', className)}
        {...props}
      />
    );
  },
);

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('text-foreground font-normal', className)}
      {...props}
    />
  );
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}

function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  );
}

export {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
};
